import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Eye, Trophy, Lightbulb, Crown, Heart, UsersThree,
  Warning, Sparkle
} from '@phosphor-icons/react';

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString()}`;

const ROLE_META = {
  owner: { label: 'Owner', icon: Crown, color: '#C5A059' },
  spouse: { label: 'Spouse / Partner', icon: Heart, color: '#C53030' },
  advisor: { label: 'Read-Only Advisor', icon: Eye, color: '#4A5568' },
};

const AdvisorHouseholdView = () => {
  const { householdId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/advisor/household/${householdId}`).then(res => {
      setData(res.data);
      setLoading(false);
    }).catch(e => {
      toast.error(e.response?.data?.detail || 'Failed to load household');
      setLoading(false);
    });
  }, [householdId]);

  if (loading) {
    return <div className="flex items-center justify-center h-96 text-[#4A5568]">Loading household...</div>;
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate('/advisor')}>
          <ArrowLeft size={16} className="mr-2" /> Back to Advisor Dashboard
        </Button>
        <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-[#4A5568]">
          Household not found or access has been revoked.
        </div>
      </div>
    );
  }

  const { household, summary } = data;
  const principals = (summary.members || []).filter(m => m.role !== 'advisor');

  return (
    <div className="space-y-8 fade-in">
      <div>
        <Link
          to="/advisor"
          data-testid="back-to-advisor-dashboard"
          className="inline-flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#0A1128] mb-4"
        >
          <ArrowLeft size={16} /> Back to Advisor Dashboard
        </Link>
      </div>

      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Eye size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Read-Only View:</strong> You are viewing this household as an advisor. You cannot modify any financial data. The household owner may revoke your access at any time.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
            {household.name}
          </h1>
          <p className="text-base text-[#4A5568]">
            {principals.length} principal{principals.length !== 1 ? 's' : ''} · {summary.recent_milestones?.length || 0} milestone{(summary.recent_milestones?.length || 0) !== 1 ? 's' : ''} in the last 7 days
          </p>
        </div>
        {summary.needs_attention && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#C53030]/10 text-[#C53030] text-xs font-semibold uppercase tracking-[0.08em] rounded">
            <Warning size={14} weight="duotone" /> Needs Attention
          </span>
        )}
      </div>

      {/* Household Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#0A1128] text-white rounded-md p-5">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.1em] font-semibold mb-2">Combined Net Worth</p>
          <p className="text-2xl font-semibold ibm-plex">{fmtMoney(summary.combined_net_worth)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5 dashboard-card">
          <p className="text-[#4A5568] text-xs uppercase tracking-[0.1em] font-semibold mb-2">Wealth Health Score™</p>
          <p className="text-2xl font-semibold text-[#0A1128] ibm-plex">{summary.wealth_health_score}<span className="text-base text-[#A0AABF]">/100</span></p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5 dashboard-card">
          <p className="text-[#4A5568] text-xs uppercase tracking-[0.1em] font-semibold mb-2">Retirement Readiness</p>
          <p className="text-2xl font-semibold text-[#0A1128] ibm-plex">{summary.retirement_readiness}%</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5 dashboard-card">
          <p className="text-[#4A5568] text-xs uppercase tracking-[0.1em] font-semibold mb-2">Recent Milestones</p>
          <p className="text-2xl font-semibold text-[#C5A059] ibm-plex">{summary.recent_milestone_count}</p>
        </div>
      </div>

      {/* Members */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="advisor-members-section">
        <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4 flex items-center gap-2">
          <UsersThree size={20} weight="duotone" className="text-[#C5A059]" />
          Members
        </h2>
        <div className="space-y-2">
          {(summary.members || []).map((m) => {
            const role = ROLE_META[m.role] || ROLE_META.advisor;
            const Icon = role.icon;
            return (
              <div key={m.user_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-3">
                  <Icon size={18} weight="duotone" style={{ color: role.color }} />
                  <div>
                    <p className="text-sm font-medium text-[#1A202C]">{m.full_name}</p>
                    <p className="text-xs text-[#A0AABF]">{m.email}</p>
                  </div>
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: role.color }}>
                  {role.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Principal breakdown */}
      <div className="space-y-4">
        {principals.map((m) => {
          const d = summary.member_data?.[m.user_id];
          if (!d) return null;
          return (
            <div key={m.user_id} className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid={`advisor-principal-card-${m.user_id}`}>
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                  <h3 className="cabinet-grotesk text-lg font-medium text-[#0A1128]">{d.full_name || m.full_name}</h3>
                  <p className="text-xs text-[#4A5568]">{m.email}</p>
                </div>
                <span className="inline-flex items-center px-2.5 py-1 bg-[#C5A059]/15 text-[#0A1128] text-xs font-semibold uppercase tracking-[0.08em] rounded">
                  {d.letter_grade} · {d.wealth_health_score}/100
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Net Worth</p>
                  <p className="text-lg font-semibold text-[#0A1128] ibm-plex">{fmtMoney(d.net_worth)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Retirement Readiness</p>
                  <p className="text-lg font-semibold text-[#0A1128] ibm-plex">{d.retirement_readiness}%</p>
                  <p className="text-xs text-[#A0AABF]">{d.retirement_readiness_status}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">Recent Milestones</p>
                  <p className="text-lg font-semibold text-[#C5A059] ibm-plex">{(d.recent_milestones || []).length}</p>
                </div>
              </div>

              {/* WHS breakdown */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                {Object.entries(d.wealth_health_breakdown || {}).map(([key, val]) => (
                  <div key={key} className="bg-slate-50 rounded p-3">
                    <p className="text-xs text-[#4A5568] uppercase tracking-[0.06em]">{key.replace(/_/g, ' ')}</p>
                    <p className="text-sm font-semibold text-[#0A1128] mt-1">{val.score}/{val.max}</p>
                    <p className="text-xs text-[#A0AABF] mt-1">{val.metric}</p>
                  </div>
                ))}
              </div>

              {d.opportunities && d.opportunities.length > 0 && (
                <div className="border-t border-slate-200 pt-3 mt-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-2 flex items-center gap-1.5">
                    <Lightbulb size={14} weight="duotone" className="text-[#C5A059]" />
                    Opportunities
                  </p>
                  <div className="space-y-2">
                    {d.opportunities.map((o, idx) => (
                      <div key={idx} className="bg-[#C5A059]/5 border border-[#C5A059]/20 rounded p-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#C5A059] mb-1">{o.category}</p>
                        <p className="text-sm font-medium text-[#0A1128]">{o.headline}</p>
                        <p className="text-xs text-[#4A5568] mt-1 leading-relaxed">{o.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Recent milestones (household-wide) */}
      {summary.recent_milestones && summary.recent_milestones.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4 flex items-center gap-2">
            <Trophy size={20} weight="duotone" className="text-[#C5A059]" />
            Recent Milestones (7 days)
          </h2>
          <div className="space-y-3">
            {summary.recent_milestones.map((ms, idx) => (
              <div key={idx} className="flex items-start justify-between gap-3 pb-3 border-b border-slate-100 last:border-0">
                <div>
                  <p className="text-sm font-medium text-[#1A202C]">{ms.title}</p>
                  <p className="text-xs text-[#4A5568] mt-1">{ms.description}</p>
                  {ms.full_name && <p className="text-xs text-[#A0AABF] mt-1">{ms.full_name}</p>}
                </div>
                <span className="text-xs text-[#A0AABF] whitespace-nowrap">
                  {ms.achieved_at ? new Date(ms.achieved_at).toLocaleDateString() : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-[#4A5568] leading-relaxed">
        <strong>Informational Use Only:</strong> Computed scores, deltas, and opportunities are general planning indicators based on data the household has entered. They are not personalized investment, tax, or legal advice. Validate against each household's full circumstances before recommending action.
      </p>
    </div>
  );
};

export default AdvisorHouseholdView;
